import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck, MapPin, Lock, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { WARDS, PRIORITIES, SOLIHULL_IMAGES } from "@/lib/constants";

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
          <a href="#priorities" className="hover:text-[#0A788A] transition-colors" data-testid="nav-priorities">Priorities</a>
          <a href="#how" className="hover:text-[#0A788A] transition-colors" data-testid="nav-how">How it works</a>
          <a href="#wards" className="hover:text-[#0A788A] transition-colors" data-testid="nav-wards">Your ward</a>
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-700 transition-colors" data-testid="nav-admin">
            <Lock size={13} /> Admin
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/survey" className="hidden sm:inline-flex items-center rounded-full bg-[#12B6CF] hover:bg-[#0A788A] text-white font-semibold text-sm px-5 py-2.5 transition-colors" data-testid="header-start-survey">
            Start the survey
          </Link>
          <button className="md:hidden text-slate-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-3 text-sm font-semibold text-slate-700">
          <a href="#priorities" onClick={() => setOpen(false)} className="block">Priorities</a>
          <a href="#how" onClick={() => setOpen(false)} className="block">How it works</a>
          <a href="#wards" onClick={() => setOpen(false)} className="block">Your ward</a>
          <Link to="/admin" onClick={() => setOpen(false)} className="block text-slate-400">Admin login</Link>
          <Link to="/survey" onClick={() => setOpen(false)} className="block rounded-full bg-[#12B6CF] text-white text-center py-2.5" data-testid="mobile-start-survey">Start the survey</Link>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#0A192F] text-slate-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid gap-8 md:grid-cols-3">
        <div>
          <Logo light />
          <p className="mt-4 text-sm text-slate-400 max-w-xs leading-relaxed">
            An official local listening survey run by Reform UK Solihull. We are gathering the views of residents across all 17 wards of the borough.
          </p>
        </div>
        <div className="text-sm">
          <h4 className="font-display font-bold text-white mb-3">The Borough</h4>
          <p className="text-slate-400 leading-relaxed">
            Covering every ward in the Solihull Metropolitan Borough, from Chelmsley Wood to Knowle and Shirley to Meriden.
          </p>
        </div>
        <div className="text-sm">
          <h4 className="font-display font-bold text-white mb-3">Your privacy</h4>
          <p className="text-slate-400 leading-relaxed">
            You can complete this survey anonymously. Contact details are optional and only used to send you local updates if you ask us to.
          </p>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-2 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Reform UK Solihull · tellsolihull.co.uk</span>
          <span data-testid="promoter-line">Promoted by Samantha Gethen on behalf of Reform UK Party Ltd, 21-24 Milbank London SW1P 4QP</span>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [quickWard, setQuickWard] = useState("");

  const startSurvey = () => {
    navigate("/survey", { state: quickWard ? { ward: quickWard } : undefined });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* HERO with quick-start survey */}
      <section className="relative bg-[#0A192F] text-white overflow-hidden">
        <img src={SOLIHULL_IMAGES.aerial} alt="Aerial view of Solihull town centre" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/60" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 lg:py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-cyan-300">
              <ShieldCheck size={14} /> Official Solihull resident survey
            </div>
            <h1 className="mt-5 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Solihull, it&apos;s time <span className="text-[#12B6CF]">your priorities</span> were heard.
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-xl leading-relaxed">
              Tell us what matters most on your street and in your ward, from potholes and policing to council tax and the green belt. It takes two minutes and you can stay completely anonymous.
            </p>
            <div className="mt-6 flex items-center gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-2"><MapPin size={16} className="text-[#12B6CF]" /> All 17 wards</span>
              <span className="flex items-center gap-2"><Lock size={16} className="text-[#12B6CF]" /> Anonymous option</span>
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#12B6CF]" /> 2 minutes</span>
            </div>
          </div>

          {/* quick start card */}
          <div className="lg:col-span-6">
            <div className="rounded-2xl bg-white p-6 sm:p-7 shadow-2xl shadow-black/30 border border-white/10">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A788A]">Start here</p>
              <h2 className="mt-2 font-display font-bold text-2xl text-[#0F172A]">Have your say now</h2>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">Pick your ward to begin. You can complete the whole survey in about two minutes.</p>
              <label className="mt-5 block text-sm font-semibold text-slate-700">Your ward</label>
              <select
                value={quickWard}
                onChange={(e) => setQuickWard(e.target.value)}
                data-testid="hero-ward-select"
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-slate-800 focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none"
              >
                <option value="">Choose your ward (or select later)</option>
                {WARDS.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
              <button
                onClick={startSurvey}
                data-testid="hero-start-survey"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#12B6CF] hover:bg-[#0FA0B7] text-white font-semibold px-6 py-3.5 text-base transition-colors"
              >
                Start the survey <ArrowRight size={18} />
              </button>
              <p className="mt-3 text-center text-xs text-slate-400">No sign up needed. Fully anonymous if you wish.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRIORITIES */}
      <section id="priorities" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A788A]">What we&apos;re asking</p>
        <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl text-[#0F172A] tracking-tight max-w-2xl">
          The issues that shape daily life in Solihull.
        </h2>
        <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
          In the survey you&apos;ll rank the issues that matter most to you and your family. Here is a taste of what we&apos;re listening to.
        </p>
        <div className="mt-10 grid gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200 sm:grid-cols-2 lg:grid-cols-3">
          {PRIORITIES.slice(0, 9).map((p) => (
            <div key={p.id} className="bg-white p-6 hover:bg-slate-50 transition-colors">
              <div className="h-9 w-9 rounded-lg bg-[#E6F8FA] text-[#0A788A] flex items-center justify-center font-display font-bold">
                {p.label.charAt(0)}
              </div>
              <h3 className="mt-4 font-display font-bold text-lg text-[#0F172A]">{p.label}</h3>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS with image */}
      <section id="how" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
              <img src={SOLIHULL_IMAGES.highstreet} alt="Shoppers on a Solihull high street" className="w-full h-[360px] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 grid gap-4">
            {[
              { n: "01", t: "Choose your ward", d: "Tell us where you live so we can group feedback by neighbourhood across all 17 Solihull wards." },
              { n: "02", t: "Set your priorities", d: "Pick the local issues that matter most to you, and report anything specific that needs fixing on your street." },
              { n: "03", t: "Optional: stay in touch", d: "Leave your details if you would like local updates, or submit completely anonymously. It is your choice." },
            ].map((s) => (
              <div key={s.n} className="flex gap-5 bg-white rounded-2xl border border-slate-200 p-6">
                <span className="font-display font-extrabold text-4xl text-[#12B6CF]/30 leading-none">{s.n}</span>
                <div>
                  <h3 className="font-display font-bold text-xl text-[#0F172A]">{s.t}</h3>
                  <p className="mt-1.5 text-slate-600 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WARDS */}
      <section id="wards" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A788A]">Every corner of the borough</p>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl text-[#0F172A] tracking-tight">
              Covering all 17 Solihull wards.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Whether you&apos;re in Chelmsley Wood or Knowle, Shirley or Meriden, your voice counts. Find your ward and have your say.
            </p>
            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <img src={SOLIHULL_IMAGES.church} alt="A historic Solihull church and timber building" className="w-full h-48 object-cover" />
            </div>
            <button onClick={() => navigate("/survey")} className="mt-6 inline-flex items-center rounded-full bg-[#0A192F] hover:bg-[#12233f] text-white font-semibold px-6 py-3 transition-colors" data-testid="wards-start-survey">
              Find your ward and start <ArrowRight size={17} className="ml-2" />
            </button>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-2.5">
            {WARDS.map((w) => (
              <button
                key={w}
                onClick={() => navigate("/survey", { state: { ward: w } })}
                data-testid={`ward-link-${w}`}
                className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-[#12B6CF] hover:shadow-sm transition-all text-left"
              >
                <MapPin size={16} className="text-[#12B6CF] shrink-0" />
                <span className="text-sm font-semibold text-slate-700">{w}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#12B6CF]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-extrabold text-3xl text-white tracking-tight">Ready to be heard?</h2>
            <p className="mt-2 text-white/90 text-lg">Two minutes. Anonymous if you like. It makes a real difference.</p>
          </div>
          <button onClick={() => navigate("/survey")} className="inline-flex items-center rounded-full bg-[#0A192F] hover:bg-[#0f2547] text-white font-semibold px-8 py-4 text-base transition-colors shadow-lg" data-testid="cta-start-survey">
            Start the survey <ArrowRight size={18} className="ml-2" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
